import { useCallback, useState } from 'react';
import { Grid, GridItem, HStack, Text, VStack } from '@chakra-ui/react';

import CustomOverlayLink from 'shared/CustomOverlayLink';
import ChevronIcon from 'shared/ChevronIcon';
import ButtonGoTo from 'shared/ButtonGoTo';
import CustomModal from '../CustomModal';
import Switcher from 'shared/Switcher';

import { icon, title, labels, btnText, cardsData, description } from 'constants/Service1cModals';
import { DEFAULT_BORDER_RADIUS, DISABLE_SCROLLBAR_STYLE, PRICE_1C_LINK } from 'constants/global';

const MODAL_ID = 'ITS-modal';

const ITSModal = ({ isOpen, onClose }) => {
  const [activeLabel, setActiveLabel] = useState(labels[0]);

  const handleLabelClick = useCallback(newLabel => {
    setActiveLabel(newLabel);
    document.getElementById(MODAL_ID)?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <VStack
        id={MODAL_ID}
        w="full"
        maxH="full"
        align="start"
        gap={{ mobile: '24px', laptop: '32px', desktop: '48px' }}
        bgColor="bg.white"
        borderRadius={DEFAULT_BORDER_RADIUS}
        p={{ mobile: '16px', tablet: '20px', laptop: '52px' }}
        overflow="auto"
        sx={DISABLE_SCROLLBAR_STYLE}
        onClick={e => e.stopPropagation()}
      >
        <VStack
          w="full"
          align="start"
          pos="relative"
          gap={{ mobile: '16px', tablet: '23px', laptop: '20px', desktop: '30px' }}
        >
          <HStack
            w="full"
            justifyContent="space-between"
            alignItems="end"
            gap="12px"
            flexWrap="wrap"
          >
            <Text as="h2" textStyle="header.h3">
              {title}
            </Text>

            <ChevronIcon
              src={icon}
              boxSize="unset"
              w={{ mobile: '89px', desktop: '142px' }}
              h={{ mobile: '40px', desktop: '64px' }}
              boxShadow="0 0 0 5px #FFFFFF, 0 0 1px 6px #EAEFE8"
              borderColor="bg.white"
              boxSizing="border-box"
              bgColor="bg.neonLight"
              imageProps={{ w: '72%' }}
            />
          </HStack>

          <Text
            textStyle="h1"
            fontSize={{ mobile: '12px', tablet: '16px', xxxl: '20px', desktop: '24px' }}
            fontWeight="500"
            maxW={{ laptop: '810px' }}
          >
            {description}
          </Text>
        </VStack>

        <VStack w="full" align="start" gap={{ mobile: '20px', tablet: '16px', laptop: '24px' }}>
          <Switcher
            uniqueId="ITSContentSwitcher"
            labels={labels}
            activeLabel={activeLabel}
            onLabelClick={handleLabelClick}
            boxProps={{ pos: 'sticky', top: { mobile: '-10px', laptop: '-40px' }, zIndex: '150' }}
          />

          <Grid
            w="full"
            templateColumns={{ mobile: '1fr', md: 'repeat(3, 1fr)' }}
            autoRows={{
              mobile: 'minmax(130px, 1fr)',
              xm: 'minmax(160px, 1fr)',
              tablet: 'minmax(240px, 1fr)',
              laptop: 'minmax(260px, 1fr)',
            }}
            gap={{ mobile: '12px', laptop: '20px' }}
          >
            {cardsData[activeLabel].map(({ title, description, link }, i) => (
              <GridItem
                key={i}
                pos="relative"
                display="flex"
                flexDir="column"
                alignItems="start"
                bgColor="bg.grayLight"
                gap={{ mobile: '8px', laptop: '12px' }}
                p={{ mobile: '16px', laptop: '32px' }}
                borderRadius={DEFAULT_BORDER_RADIUS}
                transition="background-color 0.2s"
                _hover={{ laptop: { bgColor: 'bg.accent' } }}
              >
                <CustomOverlayLink title={title} link={link} />

                <Text
                  as="h3"
                  textStyle="h1"
                  fontSize={{ mobile: '16px', laptop: '18px' }}
                  fontWeight="500"
                >
                  {title}
                </Text>

                <Text textStyle="h2" fontSize={{ mobile: '12px', laptop: '16px' }}>
                  {description}
                </Text>
              </GridItem>
            ))}
          </Grid>
        </VStack>

        <ButtonGoTo size="secondary" w="full" btnText={btnText}>
          <CustomOverlayLink title={btnText} link={PRICE_1C_LINK} />
        </ButtonGoTo>
      </VStack>
    </CustomModal>
  );
};

export default ITSModal;
